import {
  ArrowUpNarrowWide,
  BookA,
  Box,
  CalendarDaysIcon,
  LayoutDashboard,
  Send,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { formatDate } from "@/utils/helper";
import { startCase } from "lodash";
import {
  AssetCategories,
  ENUM_ASSET_REQUEST_STATUS,
  ENUM_PRIORITY_OPTIONS,
} from "@/utils/enums";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/v2/spinner";
import { RequestDetailsHeader } from "@/components/RequestDetailsHeader";
import {
  RequestDetailLayout,
  RequestInfoGrid,
  approvalFlowTab,
  type RequestInfoItem,
} from "@/components/request-detail";
import { useMsGetAssetRequestDetailsByIdQuery } from "@/services/(my-space)/api.assets";
import CancelAssetRequestDialog from "./CancelAssetRequestDialog";
import AssetInfo from "@/components/workflow/_components/AssetInfo";
import { useRequestActions } from "@/hooks/useRequestActions";

/** AssetCategories values. Falls back to a humanized slug for anything else. */
const ASSET_CATEGORY_KEYS: Record<string, string> = {
  [AssetCategories.Regular]: "assets:regular",
  [AssetCategories.Consumable]: "assets:consumable",
};
const assetCategoryLabel = (
  t: (key: string) => string,
  category?: string | null,
) =>
  category
    ? ASSET_CATEGORY_KEYS[category]
      ? t(ASSET_CATEGORY_KEYS[category])
      : startCase(category)
    : "";

/** ENUM_PRIORITY_OPTIONS values. Falls back to a humanized slug for anything else. */
const PRIORITY_KEYS: Record<string, string> = {
  [ENUM_PRIORITY_OPTIONS.Low]: "common:priority.low",
  [ENUM_PRIORITY_OPTIONS.Medium]: "common:priority.medium",
  [ENUM_PRIORITY_OPTIONS.High]: "common:priority.high",
  [ENUM_PRIORITY_OPTIONS.Urgent]: "common:priority.urgent",
};
const priorityLabel = (t: (key: string) => string, priority?: string | null) =>
  priority
    ? PRIORITY_KEYS[priority]
      ? t(PRIORITY_KEYS[priority])
      : startCase(priority)
    : "";

export const MSAssetRequestView = () => {
  const { t } = useTranslation(["my-space", "assets", "common"]);

  const navigate = useNavigate();
  const params = useParams();
  const assetRequestId = Number(params.id);

  const cancelActions = useRequestActions<number>(["cancel"]);

  const { data: assetData, isFetching: isLoading } =
    useMsGetAssetRequestDetailsByIdQuery(
      { requestId: assetRequestId },
      { skip: !assetRequestId, refetchOnMountOrArgChange: true },
    );

  const assetRequest = assetData?.data?.assetRequest;

  if (isLoading) return <Spinner />;

  const infoItems: RequestInfoItem[] = [
    {
      key: "requestId",
      icon: <Send className="text-info size-7" />,
      tone: "blue",
      iconBg: "bg-info-muted",
      label: t("ui.request_id"),
      value: (
        <span className="underline cursor-pointer text-primary">
          {assetRequest?.requestId ?? "-"}
        </span>
      ),
    },
    {
      key: "category",
      icon: <LayoutDashboard className="text-primary size-7" />,
      tone: "purple",
      label: t("shared.category"),
      value: (
        <span className="capitalize">
          {assetCategoryLabel(t, assetRequest?.assetCategory) || "-"}
        </span>
      ),
    },
    {
      key: "item",
      icon: <Box className="text-success size-7" />,
      tone: "green",
      label: t("ui.item"),
      value:
        assetRequest?.assetType?.name ?? assetRequest?.consumable?.name ?? "-",
    },
    {
      key: "priority",
      icon: <ArrowUpNarrowWide className="text-danger size-7" />,
      tone: "orange",
      label: t("ui.priority"),
      value: (
        <span className="capitalize">
          {priorityLabel(t, assetRequest?.priority) || "-"}
        </span>
      ),
    },
    {
      key: "requiredDate",
      icon: <CalendarDaysIcon className="text-success size-7" />,
      tone: "green",
      label: t("ui.required_date"),
      value: assetRequest?.requiredDate
        ? formatDate(assetRequest.requiredDate)
        : "-",
    },
    {
      key: "description",
      icon: <BookA className="text-warning size-7" />,
      tone: "amber",
      label: t("ui.description"),
      value: assetRequest?.description ?? assetRequest?.reason ?? "-",
    },
  ];

  return (
    <RequestDetailLayout
      sidebarTabs={[
        approvalFlowTab({
          approvalSteps: assetData?.data?.approvalSteps,
          triggerContent: (
            <AssetInfo
              assetTypeName={
                assetData?.data?.approvalSteps?.startingPoint?.condition
                  ?.assetTypeName
              }
              requestType={
                assetData?.data?.approvalSteps?.startingPoint?.condition
                  ?.requestType
              }
              description={
                assetData?.data?.approvalSteps?.startingPoint?.condition
                  ?.description
              }
              requestDate={assetRequest?.requestDate ?? assetRequest?.createdAt}
            />
          ),
        }),
      ]}
      dialogs={
        <CancelAssetRequestDialog
          open={cancelActions.isOpen("cancel")}
          onClose={() => cancelActions.close("cancel")}
          requestId={cancelActions.getData("cancel") ?? null}
        />
      }
      detailContent={
        <div className="flex flex-col flex-1 min-h-0 bg-white">
          <RequestDetailsHeader
            title={t("assets:my_space.request_details")}
            status={assetRequest?.status?.toLowerCase()}
            onBack={() => navigate(-1)}
            className="rounded-none"
          />

          <section className="flex flex-col gap-8 p-6 flex-1 overflow-auto">
            <RequestInfoGrid items={infoItems} />
          </section>

          <div className="flex justify-end gap-3 pt-4 px-6 pb-4 border-t mt-auto">
            <Button
              variant="danger"
              disabled={
                assetRequest?.status !== ENUM_ASSET_REQUEST_STATUS.PENDING
              }
              onClick={() =>
                cancelActions.open("cancel", assetRequest?.id ?? 0)
              }
            >
              {t("ui.withdraw")}
            </Button>
          </div>
        </div>
      }
    />
  );
};
